import type { UseFormReturn } from "react-hook-form";

import { useEffect, useRef } from "react";

import type { NoteFormValues } from "@/features/projects/project/internalNotes/types";

import { toast } from "@/components/toast/toast-queue";

import { writeNoteDraft } from "../utils/noteDraftStorage";

export function useAutosaveNoteDraft(
  requestId: number,
  userId: number | undefined,
  formMethods: UseFormReturn<NoteFormValues>,
): void {
  const timeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null);
  const saveRef = useRef<(values: NoteFormValues) => void>();

  saveRef.current = (values) => {
    writeNoteDraft(requestId, userId, values);

    if (values.name || values.description) {
      toast.add({
        description: "Your note has been saved as a draft.",
        timeout: 2000,
        title: "Draft saved",
        type: "success",
      });
    }
  };

  useEffect(() => {
    const subscription = formMethods.watch((values) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        saveRef.current?.(values as NoteFormValues);
      }, 500);
    });

    return () => {
      subscription.unsubscribe();

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        saveRef.current?.(formMethods.getValues());
      }
    };
  }, []);
}
